import { graphqlClient } from '@/lib/graphql-client';

export type AccessReference = { Type: 'role' | 'permission'; Value: string };

export type ApiKeyScopeRule = {
  ItemId: string;
  OrganizationId: string;
  RoleSlug: string;
  GroupKey: string;
  GroupLabel: string;
  Label: string;
  IsGroupAll: boolean;
  SortOrder: number;
  IsVisible: boolean;
  IsEnabled: boolean;
  Includes: AccessReference[];
  Requires: AccessReference[];
};

export type ApiKeyScopeRuleInput = Omit<ApiKeyScopeRule, 'ItemId' | 'OrganizationId'>;

type MutationResult = {
  acknowledged: boolean;
  itemId?: string;
  totalImpactedData: number;
  message?: string | null;
};

const FIELDS = `
  ItemId OrganizationId RoleSlug GroupKey GroupLabel Label
  IsGroupAll SortOrder IsVisible IsEnabled
  Includes { Type Value }
  Requires { Type Value }
`;

const LIST_RULES = `
  query ListApiKeyScopeRules($where: ApiKeyScopeRuleFilterInput!) {
    getApiKeyScopeRules(where: $where, order: [], paging: { pageNo: 1, pageSize: 100 }) {
      items { ${FIELDS} }
      totalCount pageNo pageSize totalPages hasNextPage hasPreviousPage
    }
  }
`;

const INSERT_RULE = `
  mutation InsertApiKeyScopeRule($input: ApiKeyScopeRuleInsertInput!) {
    insertApiKeyScopeRule(input: $input) {
      acknowledged itemId totalImpactedData message
    }
  }
`;

const UPDATE_RULE = `
  mutation UpdateApiKeyScopeRule(
    $where: ApiKeyScopeRuleFilterInput!
    $input: ApiKeyScopeRuleUpdateInput!
  ) {
    updateApiKeyScopeRule(where: $where, input: $input) {
      acknowledged itemId totalImpactedData message
    }
  }
`;

const DELETE_RULE = `
  mutation DeleteApiKeyScopeRule(
    $where: ApiKeyScopeRuleFilterInput!
    $input: ApiKeyScopeRuleDeleteInput!
  ) {
    deleteApiKeyScopeRule(where: $where, input: $input) {
      acknowledged itemId totalImpactedData message
    }
  }
`;

const assertMutation = (result: MutationResult | undefined, action: string) => {
  if (!result || (!result.acknowledged && !result.itemId)) {
    throw new Error(result?.message || `Could not ${action} the scope rule.`);
  }
};

export async function listApiKeyScopeRules(organizationId: string): Promise<ApiKeyScopeRule[]> {
  if (!organizationId) throw new Error('Select an organization to manage scope rules.');
  const response = await graphqlClient.query<{
    getApiKeyScopeRules: { items: ApiKeyScopeRule[] };
  }>({
    query: LIST_RULES,
    variables: {
      where: { OrganizationId: { eq: organizationId } },
    },
  });
  return [...(response.getApiKeyScopeRules?.items ?? [])].sort(
    (left, right) => left.SortOrder - right.SortOrder || left.RoleSlug.localeCompare(right.RoleSlug)
  );
}

export async function createApiKeyScopeRule(input: ApiKeyScopeRuleInput): Promise<void> {
  const response = await graphqlClient.mutate<{ insertApiKeyScopeRule: MutationResult }>({
    query: INSERT_RULE,
    variables: { input },
  });
  assertMutation(response.insertApiKeyScopeRule, 'create');
}

export async function updateApiKeyScopeRule(
  itemId: string,
  input: ApiKeyScopeRuleInput
): Promise<void> {
  const response = await graphqlClient.mutate<{ updateApiKeyScopeRule: MutationResult }>({
    query: UPDATE_RULE,
    variables: { where: { ItemId: { eq: itemId } }, input },
  });
  assertMutation(response.updateApiKeyScopeRule, 'update');
}

export async function deleteApiKeyScopeRule(itemId: string): Promise<void> {
  const response = await graphqlClient.mutate<{ deleteApiKeyScopeRule: MutationResult }>({
    query: DELETE_RULE,
    variables: { where: { ItemId: { eq: itemId } }, input: { isHardDelete: false } },
  });
  assertMutation(response.deleteApiKeyScopeRule, 'delete');
}
